import FormControlLabel from "@mui/material/FormControlLabel";
import Switch from "@mui/material/Switch";
import Typography from "@mui/material/Typography";
import Grid from "@mui/material/Grid";
import { styled } from "@mui/material/styles";
import { downloadFile } from "common/helpers/utils";
import Guid from "common/values/guid/guid";
import DocumentAPIService from "documents/entities/document/api/document-api-service";
import { enqueueSnackbar } from "notistack";
import React from "react";
import { useSession } from "users/session/session-context";
import WorkAgreement, { Masked, WorkAgreementFieldCategory } from "work/entities/work-agreement/work-agreement";
import DocumentListReadonly from "work/values/work-document/view/document-list-readonly";
import {
  WorkDocumentType,
} from "work/values/work-document/work-document";

const ListContainer = styled(
  Grid,
  {
    shouldForwardProp: (prop) => prop !== "waived",
  }
)<{ waived: boolean }>(
  ({ waived }) => ({
    flexWrap: "nowrap",
    minHeight: "20rem",
    opacity: waived ? 0.25 : 1,
    pointerEvents: waived ? "none" : "auto",
    width: "100%",
  }));
const DocumentList = styled(DocumentListReadonly)(
  ({ theme }) => ({
    [theme.breakpoints.down("lg")]: {
      marginTop: theme.spacing(2),
    },
    maxHeight: "60vh",
    marginLeft: theme.spacing(2),
    overflowY: "auto",
  }));
const HeadControls = styled("div")(
  ({ theme }) => ({
    display: "flex",
    justifyContent: "space-between",
    paddingLeft: theme.spacing(2),
  }));
const HelperText = styled(Typography)(
  ({ theme }) => ({
    marginTop: theme.spacing(1),
    color: theme.palette.text.secondary,
  }));

type ConflictsTabProps = {
  activeTab: WorkAgreementFieldCategory;
  workAgreement: WorkAgreement;
};

export default function ConflictsTab(props: Readonly<ConflictsTabProps>) {
  const {
    workAgreement,
    activeTab
  } = props;

  const [isDownloadingFile, setIsDownloadingFile] = React.useState<Guid | null>(null);

  const session = useSession();

  async function handleDownloadDocumentById(
    documentId: Guid,
    event?: React.MouseEvent<HTMLButtonElement, MouseEvent>
  ): Promise<void> {
    event?.stopPropagation();
    try {
      setIsDownloadingFile(documentId);
      const documentApiService = new DocumentAPIService(session);
      const conflictsDocument = await documentApiService.downloadDocument(
        documentId
      );
      await downloadFile(conflictsDocument);
    } catch (err) {
      console.error(err);
      enqueueSnackbar(
        "Failed to download document. Please try again later.",
        { variant: "error" }
      );
    } finally {
      setIsDownloadingFile(null);
    }
  }

  if (activeTab !== WorkAgreementFieldCategory.Conflicts) return null;
  if (!workAgreement) return null;
  if (Masked.isMasked(workAgreement.conflictsCheckWaived) ||
    Masked.isMasked(workAgreement.conflictsDocuments)) {
    return "You are not permitted to view contracts."
  }

  return (
    <>
      <HeadControls>
        <FormControlLabel
          control={
            <Switch
              disabled={true}
              checked={workAgreement.conflictsCheckWaived.value ?? false}
              color="primary"
            />
          }
          label={
            workAgreement.conflictsCheckWaived.value ? (
              <strong>Conflicts Check Waived</strong>
            ) : (
              "Conflicts Check Not Waived"
            )
          }
        />
      </HeadControls>
      <ListContainer
        waived={workAgreement.conflictsCheckWaived.value ?? false}
        container
        direction="row"
      >
        <Grid size={4}>
          {workAgreement.conflictsDocuments.length === 0 &&
            <HelperText>
              No conflicts documents for this work agreement.
            </HelperText>
          }{workAgreement.conflictsDocuments.length > 0 &&
            <DocumentList
              documents={workAgreement.conflictsDocuments ?? []}
              documentType={WorkDocumentType.Conflicts}
              downloadingFileId={isDownloadingFile}
              disableCommentsPane={true}
              onDownload={handleDownloadDocumentById}
            />}
        </Grid>
      </ListContainer>
    </>
  );
}
